package hk.com.vr360_demo2.webapp.core.either


sealed class Either<out L, out R> {
    data class Left<out L>(val value: L) : Either<L, Nothing>(){
        public companion object
    }
    data class Right<out R>(val value: R) : Either<Nothing, R>()
    fun isLeft(): Boolean {
        return this@Either is Left<L>
    }
    fun isRight(): Boolean = this is Right<R>

    public inline fun isLeft(predicate: (L) -> Boolean): Boolean {
        return this@Either is Left<L> && predicate(value)
    }
    public inline fun isRight(predicate: (R) -> Boolean): Boolean {
        return this@Either is Right<R> && predicate(value)
    }
    // Core functions
    inline fun <C> fold(ifLeft: (L) -> C, ifRight: (R) -> C): C = when (this) {
        is Left -> ifLeft(value)
        is Right -> ifRight(value)
    }

    fun swap(): Either<R, L> = fold({ Right(it) }, { Left(it) })

    inline fun <C> map(f: (R) -> C): Either<L, C> = flatMap { Right(f(it)) }

    inline fun <C> mapLeft(f: (L) -> C): Either<C, R> = when (this) {
        is Left -> Left(f(value))
        is Right -> this
    }
    inline fun onLeft(action: (L) -> Unit): Either<L, R> = also {
        when (it) {
            is Left -> action(it.value)
            is Right -> Unit
        }
    }
    inline fun onRight(action: (R) -> Unit): Either<L, R> = also {  when (it) {
        is Left -> Unit
        is Right -> action(it.value)
    }}

    public fun getOrNull(): R? {

        return getOrElse { null }
    }

    fun leftOrNull(): L? = fold({ it }, { null })

    inline fun <C> flatMap(f: (R) -> Either<@UnsafeVariance L, C>): Either<L, C> = when (this) {
        is Left -> this
        is Right -> f(value)
    }

    inline fun <C> handleErrorWith(f: (L) -> Either<C, @UnsafeVariance R>): Either<C, R> = when (this) {
        is Left -> f(value)
        is Right -> this
    }

    // Extended functionality










    inline fun <C> bimap(leftF: (L) -> C, rightF: (R) -> C): C = fold(leftF, rightF)

    inline fun <C> foldLeft(initial: C, f: (C, R) -> C): C = fold({ initial }, { f(initial, it) })




    // Companion object with constructors and utilities
    companion object {
        fun <L> left(value: L): Either<L, Nothing> = Left(value)
        fun <R> right(value: R): Either<Nothing, R> = Right(value)

        inline fun <R> catch(f: () -> R): Either<Throwable, R> = try {
            right(f())
        } catch (e: Throwable) {
            left(e)
        }

        inline fun <L, R> fromNullable(value: R?, ifNull: () -> L): Either<L, R> =
            value?.let { right(it) } ?: left(ifNull())

        fun <L, R> fromOption(option: Option<R>, ifNone: () -> L): Either<L, R> =
            option.fold({ left(ifNone()) }, { right(it) })
    }
}

// Extension functions
fun <L> L.left(): Either<L, Nothing> = Either.left(this)
fun <R> R.right(): Either<Nothing, R> = Either.right(this)

fun <L, R, C> Either<L, R>.flatMapLeft(f: (L) -> Either<C, R>): Either<C, R> = when (this) {
    is Either.Left -> f(value)
    is Either.Right -> this
}

inline fun <L, R> Either<L, R>.getOrElse(default: (L) -> R): R = fold(default) { it }

inline fun <L, R> Either<L, R>.orElse(default: () -> Either<L, R>): Either<L, R> = fold({ default() }, { this })

fun <L, R, C> Either<L, R>.andThen(other: Either<L, C>): Either<L, C> = flatMap { other }

fun <L, R, C> Either<L, R>.and(other: Either<L, C>): Either<L, Pair<R, C>> =
    flatMap { r -> other.map { c -> r to c } }





